// src/features/invoice/components/InvoiceManager.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useInvoice } from "../hooks/useInvoice";
import InvoiceStats from "./InvoiceStats";
import InvoiceList from "./InvoiceList";
import InvoiceForm from "./InvoiceForm";
import InvoicePreview from "./InvoicePreview";
import InvoiceFilter from "./InvoiceFilter";

const InvoiceManager = () => {
  const {
    invoices,
    loading,
    error,
    success,
    filters,
    stats,
    getInvoices,
    createNewInvoice,
    updateExistingInvoice,
    deleteExistingInvoice,
    updateStatus,
    sendInvoiceToClient,
    recordPaymentForInvoice,
    updateFilters,
    clearAllFilters,
    clearErrorMessage,
    clearSuccessMessage,
  } = useInvoice();

  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Auto-refresh invoices when filters change
  useEffect(() => {
    getInvoices();
  }, [filters]);

  // Handle snackbar
  useEffect(() => {
    if (error || success) {
      setSnackbarOpen(true);
    }
  }, [error, success]);

  const handleCreateInvoice = async (data) => {
    try {
      await createNewInvoice(data);
      setShowForm(false);
      getInvoices();
    } catch (err) {
      console.error("Create failed:", err);
    }
  };

  const handleUpdateInvoice = async (data) => {
    try {
      await updateExistingInvoice(editingInvoice.id, data);
      setShowForm(false);
      setEditingInvoice(null);
      getInvoices();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (window.confirm("Delete this invoice?")) {
      try {
        await deleteExistingInvoice(id);
        getInvoices();
      } catch (err) {
        console.error("Delete failed:", err);
      }
    }
  };

  const handleSendInvoice = async (id) => {
    try {
      await sendInvoiceToClient(id);
      getInvoices();
    } catch (err) {
      console.error("Send failed:", err);
    }
  };

  const handleRecordPayment = async (id, paymentData) => {
    try {
      await recordPaymentForInvoice(id, paymentData);
      getInvoices();
      setShowPreview(false);
    } catch (err) {
      console.error("Payment failed:", err);
    }
  };

  const handlePreview = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    clearErrorMessage();
    clearSuccessMessage();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          Invoice Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingInvoice(null);
            setShowForm(true);
          }}
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          New Invoice
        </Button>
      </Box>

      {/* Stats */}
      <InvoiceStats stats={stats} />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <InvoiceFilter
          filters={filters}
          onFilterChange={updateFilters}
          onClear={clearAllFilters}
        />
      </Paper>

      {/* List */}
      <InvoiceList
        invoices={invoices}
        loading={loading}
        onEdit={(invoice) => {
          setEditingInvoice(invoice);
          setShowForm(true);
        }}
        onDelete={handleDeleteInvoice}
        onSend={handleSendInvoice}
        onPreview={handlePreview}
        onRecordPayment={handleRecordPayment}
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingInvoice(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingInvoice ? "Edit Invoice" : "Create New Invoice"}
        </DialogTitle>
        <DialogContent>
          <InvoiceForm
            initialData={editingInvoice}
            onSubmit={
              editingInvoice ? handleUpdateInvoice : handleCreateInvoice
            }
            onCancel={() => {
              setShowForm(false);
              setEditingInvoice(null);
            }}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <InvoicePreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        invoice={selectedInvoice}
        onSend={handleSendInvoice}
        onRecordPayment={handleRecordPayment}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceManager;
